/**
 * BehaviorNavigation — extracted from BehaviorEngine.ts
 *
 * All navigation / movement functions for robot AI:
 * BFS pathfinding, flying movement, patrol, unified dispatchers.
 */

import { Robot } from '@/entities/Robot';
import { setMoveIntent } from '@/systems/RobotPhysics';
import { MAP_GRID, getDecorAt } from '@/map/MapData';
import { DECOR_INTERACTIONS, DecorInteraction, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';
import { TILE_SIZE, MAP_COLS, MAP_ROWS, TileType, GAME_WIDTH, GAME_HEIGHT } from '@/config/GameConfig';
import { ComponentCategory } from '@/components/ComponentDefs';
import { GOD } from '@/config/GodMode';

// ── Module-level state ──

const DIRS: [number, number][] = [[0, -1], [0, 1], [-1, 0], [1, 0]];
let walkableTileCoords: { col: number; row: number }[] | null = null;
export const robotPaths = new Map<string, RobotPathState>();
export const patrolTargets = new Map<string, { goalCol: number; goalRow: number }>();
const flybyState = new Map<string, { loopX: number; loopY: number; phase: 'charge' | 'loop' }>();
const PURSUE_MIN_DIST = TILE_SIZE * 2;
const PURSUE_MAX_DIST = TILE_SIZE * 4;
const FLYBY_LOOP_RADIUS = TILE_SIZE * 4;

// Module-level allies ref — set each tick so movement functions can avoid other robots
let currentAllies: Robot[] = [];

// ── Per-robot path state ──

export interface RobotPathState {
  path: { col: number; row: number }[];
  pathIdx: number;
  goalCol: number;
  goalRow: number;
}

// ── Allies accessors ──

export function setCurrentAllies(allies: Robot[]): void {
  currentAllies = allies;
}

export function getCurrentAllies(): Robot[] {
  return currentAllies;
}

// ── Cache reset ──

/** Clear all navigation-related cached state — call when switching maps */
export function resetNavigationCaches(): void {
  walkableTileCoords = null;
  robotPaths.clear();
  patrolTargets.clear();
  flybyState.clear();
}

// ── Tile checks ──

// Check if a tile is walkable for robots (PATH, PLACEMENT, MUD, GRILLE)
export function isRobotTile(col: number, row: number, allowHarvestable = false): boolean {
  if (col < 0 || col >= MAP_COLS || row < 0 || row >= MAP_ROWS) return false;
  const t = MAP_GRID[row][col];
  if (t !== TileType.PATH && t !== TileType.PLACEMENT && t !== TileType.BROKEN_PLACEMENT &&
      t !== TileType.MUD && t !== TileType.GRILLE && t !== TileType.GROUND) return false;
  // Check blocking decoration
  const decor = getDecorAt(col, row);
  if (decor && DECOR_INTERACTIONS[decor.type].interaction === DecorInteraction.BLOCK) {
    // Excavator robots can path through harvestable resources (they'll harvest on arrival)
    if (allowHarvestable && DECOR_RESOURCE_YIELDS[decor.type]) return true;
    return false;
  }
  return true;
}

/** SPAWN / BASE tiles are not normally walkable, but robots can path TO them (e.g. kamikaze). */
export function isTargetableTile(col: number, row: number): boolean {
  if (col < 0 || col >= MAP_COLS || row < 0 || row >= MAP_ROWS) return false;
  const t = MAP_GRID[row][col];
  return t === TileType.SPAWN || t === TileType.BASE;
}

// ── Tile-center helpers ──

export function tileCenter(col: number, row: number): { x: number; y: number } {
  return { x: col * TILE_SIZE + TILE_SIZE / 2, y: row * TILE_SIZE + TILE_SIZE / 2 };
}

export function pixelToTile(px: number, py: number): { col: number; row: number } {
  return { col: Math.floor(px / TILE_SIZE), row: Math.floor(py / TILE_SIZE) };
}

// ── BFS pathfinding (cardinal directions only) ──

// Build a set of tile keys occupied by other robots (for avoidance in BFS)
export function buildOccupiedTiles(allies: Robot[], selfId: string): Set<number> {
  const occupied = new Set<number>();
  for (const ally of allies) {
    if (!ally.alive || ally.id === selfId || ally.hasWings()) continue;
    const col = Math.floor(ally.x / TILE_SIZE);
    const row = Math.floor(ally.y / TILE_SIZE);
    occupied.add(row * MAP_COLS + col);
  }
  return occupied;
}

/**
 * BFS pathfinding with optional robot avoidance.
 * @param blocked — tile keys occupied by other robots (treated as non-walkable)
 */
export function bfsPath(
  startCol: number, startRow: number,
  goalCol: number, goalRow: number,
  blocked?: Set<number>,
  allowHarvestable = false,
): { col: number; row: number }[] | null {
  if (startCol === goalCol && startRow === goalRow) return [];
  // Allow SPAWN/BASE as goal (kamikaze robots need to reach enemy bases)
  if (!isRobotTile(goalCol, goalRow, allowHarvestable) && !isTargetableTile(goalCol, goalRow)) return null;

  const key = (c: number, r: number) => r * MAP_COLS + c;
  const visited = new Set<number>();
  const parent = new Map<number, number>();
  const queue: number[] = [];

  const startKey = key(startCol, startRow);
  const goalKey = key(goalCol, goalRow);
  visited.add(startKey);
  queue.push(startKey);

  let head = 0;
  while (head < queue.length) {
    const cur = queue[head++];
    const cr = Math.floor(cur / MAP_COLS);
    const cc = cur % MAP_COLS;

    for (const [dc, dr] of DIRS) {
      const nc = cc + dc;
      const nr = cr + dr;
      // Allow goal tile even if SPAWN/BASE (for kamikaze reaching enemy bases)
      if (!isRobotTile(nc, nr, allowHarvestable) && !(nc === goalCol && nr === goalRow)) continue;
      const nk = key(nc, nr);
      if (visited.has(nk)) continue;
      // Avoid tiles occupied by other robots (but always allow the goal tile)
      if (blocked && nk !== goalKey && blocked.has(nk)) continue;
      visited.add(nk);
      parent.set(nk, cur);
      if (nk === goalKey) {
        const path: { col: number; row: number }[] = [];
        let k = goalKey;
        while (k !== startKey) {
          path.push({ col: k % MAP_COLS, row: Math.floor(k / MAP_COLS) });
          k = parent.get(k)!;
        }
        path.reverse();
        return path;
      }
      queue.push(nk);
    }
  }

  // No path found with avoidance — retry without blocked tiles as fallback
  if (blocked && blocked.size > 0) {
    return bfsPath(startCol, startRow, goalCol, goalRow, undefined, allowHarvestable);
  }
  return null;
}

// ── Path following ──

// Move robot along its current path — sets movement intent toward next waypoint
export function followPath(robot: Robot, allies: Robot[], dt: number): boolean {
  const state = robotPaths.get(robot.id);
  if (!state || state.pathIdx >= state.path.length) return false;

  const target = state.path[state.pathIdx];

  // Check if the next waypoint tile is occupied by another robot → invalidate path
  for (const ally of allies) {
    if (!ally.alive || ally.id === robot.id || ally.hasWings()) continue;
    const allyCol = Math.floor(ally.x / TILE_SIZE);
    const allyRow = Math.floor(ally.y / TILE_SIZE);
    if (allyCol === target.col && allyRow === target.row) {
      // Path blocked — clear it so navigateTo re-paths next frame with avoidance
      robotPaths.delete(robot.id);
      return false;
    }
  }

  const center = tileCenter(target.col, target.row);
  const dx = center.x - robot.x;
  const dy = center.y - robot.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Detect overshoot: if robot velocity points AWAY from waypoint, it has passed it.
  // This prevents oscillation at high game speeds (x4) where step > 2px threshold.
  const movingAway = (robot.vx * dx + robot.vy * dy) < 0;

  if (dist < 2 || (movingAway && dist < TILE_SIZE * 0.5)) {
    // Close enough or overshot waypoint — advance to next step
    state.pathIdx++;
    if (state.pathIdx >= state.path.length) return false;
    // Set intent toward next waypoint
    const next = state.path[state.pathIdx];
    const nc = tileCenter(next.col, next.row);
    const ndx = nc.x - robot.x;
    const ndy = nc.y - robot.y;
    const nd = Math.sqrt(ndx * ndx + ndy * ndy);
    if (nd > 0.1) {
      setMoveIntent(robot, ndx / nd, ndy / nd, robot.speed);
    }
  } else {
    // Set intent toward current waypoint
    setMoveIntent(robot, dx / dist, dy / dist, robot.speed);
  }

  // Movement is free — energy consumed per action only
  return true;
}

// Navigate robot toward a pixel target using BFS pathfinding (with robot avoidance)
export function navigateTo(robot: Robot, tx: number, ty: number, dt: number, allowHarvestable = false): void {
  const cur = pixelToTile(robot.x, robot.y);
  const goal = pixelToTile(tx, ty);

  const state = robotPaths.get(robot.id);

  // Re-path if no path, different goal, or path exhausted
  if (!state || state.goalCol !== goal.col || state.goalRow !== goal.row || state.pathIdx >= state.path.length) {
    const blocked = currentAllies.length > 0 ? buildOccupiedTiles(currentAllies, robot.id) : undefined;
    const path = bfsPath(cur.col, cur.row, goal.col, goal.row, blocked, allowHarvestable);
    if (path && path.length > 0) {
      robotPaths.set(robot.id, { path, pathIdx: 0, goalCol: goal.col, goalRow: goal.row });
    } else {
      robotPaths.delete(robot.id);
      return;
    }
  }

  followPath(robot, currentAllies, dt);
}

// ── Flying movement (wings): direct pixel movement, all directions, no obstacles ──

export function clampToMap(x: number, y: number): { x: number; y: number } {
  const margin = TILE_SIZE / 2;
  return {
    x: Math.max(margin, Math.min(GAME_WIDTH - margin, x)),
    y: Math.max(margin, Math.min(GAME_HEIGHT - margin, y)),
  };
}

export function flyToward(robot: Robot, tx: number, ty: number, dt: number): void {
  const dx = tx - robot.x;
  const dy = ty - robot.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return;

  setMoveIntent(robot, dx / dist, dy / dist, robot.speed);
  // Movement is free — energy consumed per action only
}

export function flyAwayFrom(robot: Robot, tx: number, ty: number, dt: number): void {
  const dx = robot.x - tx;
  const dy = robot.y - ty;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 0.01) return;

  setMoveIntent(robot, dx / dist, dy / dist, robot.speed);
  // Movement is free — energy consumed per action only
}

// ── Pursue at distance: maintain 2–4 tiles from target ──

export function pursueAtDistance(robot: Robot, tx: number, ty: number, dt: number): void {
  const dx = tx - robot.x;
  const dy = ty - robot.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // ── Flying robots: strafing run (charge → loop back → charge again) ──
  if (robot.hasWings()) {
    flybyPursue(robot, tx, ty, dist, dx, dy, dt);
    return;
  }

  // ── Ground robots ──
  if (dist > PURSUE_MAX_DIST) {
    moveToward(robot, tx, ty, dt);
  } else if (dist < PURSUE_MIN_DIST) {
    moveAwayFrom(robot, tx, ty, dt);
  } else {
    // Ideal range — slowly track the target
    if (dist > 0.1) {
      setMoveIntent(robot, dx / dist, dy / dist, robot.speed * 0.3);
      // Movement is free — energy consumed per action only
    }
  }
}

/**
 * Flyby strafing run: charge at enemy, when close switch to a loop-back
 * waypoint perpendicular to approach, creating a banking turn to come back.
 */
export function flybyPursue(
  robot: Robot, tx: number, ty: number,
  dist: number, dx: number, dy: number, dt: number,
): void {
  let state = flybyState.get(robot.id);

  if (!state) {
    state = { loopX: 0, loopY: 0, phase: 'charge' };
    flybyState.set(robot.id, state);
  }

  const chargeThreshold = TILE_SIZE * 1.5; // switch to loop when this close
  const loopDoneThreshold = TILE_SIZE * 2;  // close enough to loop point = resume charge

  if (state.phase === 'charge') {
    if (dist > chargeThreshold) {
      // Charge toward target
      flyToward(robot, tx, ty, dt);
    } else {
      // Too close — compute a loop-back waypoint:
      // Pick a point perpendicular to the approach direction, behind the robot
      const hx = Math.cos(robot.heading);
      const hy = Math.sin(robot.heading);
      // Perpendicular (pick consistent side based on robot id hash for variety)
      const side = (robot.id.charCodeAt(0) % 2 === 0) ? 1 : -1;
      const perpX = -hy * side;
      const perpY = hx * side;
      // Loop point = behind + to the side
      state.loopX = robot.x - hx * FLYBY_LOOP_RADIUS + perpX * FLYBY_LOOP_RADIUS;
      state.loopY = robot.y - hy * FLYBY_LOOP_RADIUS + perpY * FLYBY_LOOP_RADIUS;
      state.phase = 'loop';
      flyToward(robot, state.loopX, state.loopY, dt);
    }
  } else {
    // Loop phase — fly toward the loop waypoint
    const ldx = state.loopX - robot.x;
    const ldy = state.loopY - robot.y;
    const ldist = Math.sqrt(ldx * ldx + ldy * ldy);

    if (ldist < loopDoneThreshold) {
      // Loop complete — resume charging
      state.phase = 'charge';
      flyToward(robot, tx, ty, dt);
    } else {
      flyToward(robot, state.loopX, state.loopY, dt);
    }
  }
}

// ── Unified movement dispatchers ──

export function moveToward(robot: Robot, tx: number, ty: number, dt: number, allowHarvestable = false): void {
  if (robot.hasWings()) {
    flyToward(robot, tx, ty, dt);
  } else {
    navigateTo(robot, tx, ty, dt, allowHarvestable);
  }
}

export function moveAwayFrom(robot: Robot, tx: number, ty: number, dt: number): void {
  if (robot.hasWings()) {
    flyAwayFrom(robot, tx, ty, dt);
    return;
  }
  // Ground: pick the walkable neighbor tile farthest from threat
  const cur = pixelToTile(robot.x, robot.y);
  let bestCol = cur.col;
  let bestRow = cur.row;
  let bestDist = -1;

  for (const [dc, dr] of DIRS) {
    const nc = cur.col + dc;
    const nr = cur.row + dr;
    if (!isRobotTile(nc, nr)) continue;
    const center = tileCenter(nc, nr);
    const d = Math.abs(center.x - tx) + Math.abs(center.y - ty);
    if (d > bestDist) {
      bestDist = d;
      bestCol = nc;
      bestRow = nr;
    }
  }

  if (bestCol !== cur.col || bestRow !== cur.row) {
    const center = tileCenter(bestCol, bestRow);
    navigateTo(robot, center.x, center.y, dt);
  }
}

// ── Patrol / Exploration: pick random walkable destinations across the map ──

export function getWalkableTileCoords(): { col: number; row: number }[] {
  if (walkableTileCoords) return walkableTileCoords;
  walkableTileCoords = [];
  for (let r = 0; r < MAP_ROWS; r++) {
    for (let c = 0; c < MAP_COLS; c++) {
      if (isRobotTile(c, r)) {
        walkableTileCoords.push({ col: c, row: r });
      }
    }
  }
  return walkableTileCoords;
}

export function pickRandomWalkableTile(curCol: number, curRow: number): { col: number; row: number } {
  const tiles = getWalkableTileCoords();
  const minDist = 3; // at least 3 tiles Manhattan distance
  let tries = 0;
  while (tries < 20) {
    const t = tiles[Math.floor(Math.random() * tiles.length)];
    if (Math.abs(t.col - curCol) + Math.abs(t.row - curRow) >= minDist) return t;
    tries++;
  }
  return tiles[Math.floor(Math.random() * tiles.length)];
}

/**
 * Flying patrol: pick a tile biased toward friendly base.
 * Exploration probability is inversely proportional to distance from base:
 *   close to base → high chance, far from base → low chance.
 * This creates a patrol bubble that naturally stays near defended territory.
 */
export function pickRandomMapTile(curCol: number, curRow: number, baseCol?: number, baseRow?: number): { col: number; row: number } {
  const minDist = 3;
  // If no base info, fall back to uniform random
  if (baseCol === undefined || baseRow === undefined) {
    for (let t = 0; t < 20; t++) {
      const col = Math.floor(Math.random() * MAP_COLS);
      const row = Math.floor(Math.random() * MAP_ROWS);
      if (Math.abs(col - curCol) + Math.abs(row - curRow) >= minDist) return { col, row };
    }
    return { col: Math.floor(Math.random() * MAP_COLS), row: Math.floor(Math.random() * MAP_ROWS) };
  }

  // Weighted selection: generate candidates and pick one biased toward base
  const maxMapDist = MAP_COLS + MAP_ROWS; // Manhattan diagonal of the map
  let bestCandidate = { col: baseCol, row: baseRow };
  let bestWeight = -1;

  for (let t = 0; t < 15; t++) {
    const col = Math.floor(Math.random() * MAP_COLS);
    const row = Math.floor(Math.random() * MAP_ROWS);
    // Skip tiles too close to current position
    if (Math.abs(col - curCol) + Math.abs(row - curRow) < minDist) continue;

    // Weight blends proximity-to-base with exploration desire
    // GOD.flyerAudacity: high = explores far from base, low = stays near base
    const distToBase = Math.abs(col - baseCol) + Math.abs(row - baseRow);
    const normDist = distToBase / maxMapDist; // 0..1
    const exploreWeight = Math.pow(normDist, 1 / GOD.flyerAudacity); // high audacity → favors far tiles
    const safeWeight = 1 / (1 + distToBase);                         // always some pull toward base
    const weight = (exploreWeight * GOD.flyerAudacity + safeWeight) * (0.5 + Math.random());
    if (weight > bestWeight) {
      bestWeight = weight;
      bestCandidate = { col, row };
    }
  }
  return bestCandidate;
}

export function patrol(robot: Robot, dt: number, baseX?: number, baseY?: number): void {
  const cur = pixelToTile(robot.x, robot.y);
  const isFlying = robot.hasWings();
  const baseTile = baseX !== undefined && baseY !== undefined
    ? pixelToTile(baseX, baseY) : undefined;

  if (!patrolTargets.has(robot.id)) {
    const dest = isFlying
      ? pickRandomMapTile(cur.col, cur.row, baseTile?.col, baseTile?.row)
      : pickRandomWalkableTile(cur.col, cur.row);
    patrolTargets.set(robot.id, { goalCol: dest.col, goalRow: dest.row });
  }

  const target = patrolTargets.get(robot.id)!;
  const goalCenter = tileCenter(target.goalCol, target.goalRow);

  if (isFlying) {
    // Flying: use pixel-distance arrival (no snap, smooth transition)
    const dx = goalCenter.x - robot.x;
    const dy = goalCenter.y - robot.y;
    const distToGoalSq = dx * dx + dy * dy;
    const halfTileSq = (TILE_SIZE * 0.5) * (TILE_SIZE * 0.5);

    if (distToGoalSq < halfTileSq) {
      // Close enough — pick new destination biased toward base
      const dest = pickRandomMapTile(cur.col, cur.row, baseTile?.col, baseTile?.row);
      target.goalCol = dest.col;
      target.goalRow = dest.row;
      return;
    }

    flyToward(robot, goalCenter.x, goalCenter.y, dt);
  } else {
    // Ground: tile-based arrival
    if (cur.col === target.goalCol && cur.row === target.goalRow) {
      // Arrived — pick new destination (physics will decelerate naturally)
      const dest = pickRandomWalkableTile(cur.col, cur.row);
      target.goalCol = dest.col;
      target.goalRow = dest.row;
      robotPaths.delete(robot.id);
      return;
    }

    navigateTo(robot, goalCenter.x, goalCenter.y, dt);

    // If path failed (unreachable), pick a new destination
    if (!robotPaths.has(robot.id)) {
      const dest = pickRandomWalkableTile(cur.col, cur.row);
      target.goalCol = dest.col;
      target.goalRow = dest.row;
    }
  }
}

// Movement is free — no energy drain for wheels/wings/thrusters/turbines
